import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";

import { LoadingSpinner } from "../ui";
import useAuth from "../../hooks/useAuth";
import useLogout from "../../hooks/useLogout";
import { RootState } from "../../redux/store";
import ErrorBoundary from "../error/ErrorBoundary";
import useTokenRefresh from "../../hooks/useTokenRefresh";
import { tokenRefreshLimiter } from "../../utils/rateLimiter";
import { handleAuthError } from "../../utils/authErrorHandler";

const PersistLogin = () => {
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const handleRefresh = useTokenRefresh();
  const { persist, accessToken, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const refreshInProgressRef = useRef(false);
  const { lang } = useSelector((state: RootState) => state.auth);

  const refreshToken = useCallback(async () => {
    try {
      if (!tokenRefreshLimiter.isAllowed("token-refresh")) {
        throw new Error("RATE_LIMIT_EXCEEDED");
      }

      await handleRefresh();
    } catch (err) {
      await handleAuthError(err, navigate, handleLogout, lang);
    } finally {
      setIsLoading(false);
    }
  }, [handleRefresh, navigate, handleLogout, lang]);

  useEffect(() => {
    let isMounted = true;

    const verifyRefreshToken = async () => {
      refreshInProgressRef.current = true;
      try {
        await refreshToken();
      } catch (err) {
        console.error("PersistLogin: Token refresh failed:", err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
        refreshInProgressRef.current = false;
      }
    };

    // Only attempt refresh if persist is enabled AND we have no access token
    // But only refresh if we're authenticated (prevents premature refresh on login)
    if (
      !accessToken &&
      persist &&
      isAuthenticated &&
      !refreshInProgressRef.current
    ) {
      verifyRefreshToken();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [accessToken, persist, refreshToken, isAuthenticated]);

  return (
    <ErrorBoundary>
      {!persist ? (
        <Outlet />
      ) : isLoading ? (
        <LoadingSpinner size="xxl" />
      ) : (
        <Outlet />
      )}
    </ErrorBoundary>
  );
};

export default PersistLogin;
